import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckIcon from "@material-ui/icons/Check";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import A_TextField from "../../../Atoms/Inputs/TextFields/A_TextField";
import A_SimpleSelect from "../../../Atoms/Inputs/Selects/A_SimpleSelect";
import A_DatePicker from "../../../Atoms/Inputs/DateTimePickers/A_DatePicker";
import A_Switch from "../../../Atoms/Inputs/Switches/A_Switch";
import LoaderSkeleton from "../../../Molecules/Common/LoaderSkeleton";
import {
  validateFirmDraft,
  normaliseFirmName,
  isSameFirmIdentity,
} from "../../../../../domain/professionalContacts/professionalContacts.rules";
import {
  FIRM_TYPES,
  DISTRESSED_FIRM_TYPES,
  firmTypeLabel,
} from "../../../../../domain/professionalContacts/professionalContacts.types";
import { api, toBankAccountUpsert } from "./ProfessionalContacts.helper";
import ContactRowsEditor from "./ContactRowsEditor";
import AddressRowsEditor from "./AddressRowsEditor";
import BankAccountRowsEditor from "./BankAccountRowsEditor";
import ExistingFirmDialog from "./ExistingFirmDialog";
import useStyles from "./ProfessionalContacts.styles";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";

const blankContact = (main) => ({
  _key: `new-${Date.now()}-c`,
  name: "",
  role: "",
  email: "",
  phone: "",
  isMain: main,
});
const blankAddress = (preferred) => ({
  _key: `new-${Date.now()}-a`,
  building: "",
  street: "",
  town: "",
  postcode: "",
  country: "United Kingdom",
  isPreferred: preferred,
});
const blankBankAccount = (main) => ({
  _key: `new-${Date.now()}-b`,
  beneficiaryName: "",
  bankType: "",
  accountNumber: "",
  sortCode: "",
  bankName: "",
  town: "",
  postcode: "",
  country: "",
  address: "",
  isMain: main,
});

const withRowKeys = (rows, keyPrefix) =>
  (rows || []).map((row, index) => ({
    ...row,
    _key: row.id || `${keyPrefix}-${index}`,
  }));

const epochSecondsToIso = (value) =>
  value ? new Date(value * 1000).toISOString().slice(0, 10) : null;

// eslint-disable-next-line no-unused-vars
const stripKeys = (rows) => rows.map(({ _key, ...rest }) => rest);

/**
 * R11 — the duplicate message always names the company, and names it in the casing it is STORED
 * in (what the endpoint returns), not what the user typed: when the only difference is case,
 * echoing their own input back reads as nonsense. One definition so the inline warning and the
 * validation summary cannot drift apart.
 */
const duplicateNameMessage = (existingFirmName) =>
  `A company named "${existingFirmName}" already exists.`;

const FirmForm = ({
  firmId,
  defaultType,
  initialAppend,
  onCancel,
  onSaved,
}) => {
  const classes = useStyles();
  const isEdit = Boolean(firmId);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const bodyRef = useRef(null);

  // v1.13.0 duplicate-name warning (R5/R6). `checkedName`/`checkedType` are stored alongside the
  // result so the warning can be invalidated the instant draft.name OR draft.type stops matching
  // what was actually checked — that is what clears it without a second round trip (v1.14.0,
  // FIRM-DUPLICATE-NAMES-014, R12): a warning raised for a Broker must not keep standing once the
  // user switches Type to Valuer.
  const [duplicateOf, setDuplicateOf] = useState(null);
  const [checkingName, setCheckingName] = useState(false);
  // Monotonic request id: a slow first check resolving after a faster second one must not
  // overwrite it. Only the newest request is allowed to touch state.
  const nameCheckSeq = useRef(0);
  // The name AND type this form loaded with, so an edit can tell "unchanged" from "renamed or
  // moved to another type" without consulting the (uncontrolled) input.
  const loadedNameRef = useRef("");
  const loadedTypeRef = useRef("");
  // The firm whose details are open in the read-only dialog, or null. A DIALOG rather than
  // navigation on purpose: this form holds unsaved work and there is no per-firm URL to open
  // elsewhere, so sending the user to the record would discard their draft (HLD §11, 2026-09-23).
  const [viewingFirmId, setViewingFirmId] = useState(null);

  const revealValidationSummary = () => {
    const node = bodyRef.current;
    if (node && typeof node.scrollTo === "function")
      node.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    let applied = false;
    const applyAppends = (base) => {
      if (applied) return base;
      applied = true;
      const next = { ...base };
      if (initialAppend?.appendContact)
        next.contacts = [
          ...next.contacts,
          blankContact(next.contacts.length === 0),
        ];
      if (initialAppend?.appendAddress)
        next.addresses = [
          ...next.addresses,
          blankAddress(next.addresses.length === 0),
        ];
      if (initialAppend?.appendBankAccount)
        next.bankAccounts = [
          ...next.bankAccounts,
          blankBankAccount(next.bankAccounts.length === 0),
        ];
      return next;
    };

    if (isEdit) {
      setLoading(true);
      api
        .getFirm(firmId)
        .then((firm) => {
          loadedNameRef.current = firm?.name || "";
          loadedTypeRef.current = firm?.type || "";
          setDraft(
            applyAppends({
              ...firm,
              contacts: withRowKeys(firm.contacts, "c"),
              addresses: withRowKeys(firm.addresses, "a"),
              bankAccounts: withRowKeys(firm.bankAccounts, "b"),
            })
          );
        })
        .catch((err) => setLoadError(err))
        .finally(() => setLoading(false));
    } else {
      setDraft(
        applyAppends({
          type:
            defaultType && FIRM_TYPES.includes(defaultType)
              ? defaultType
              : FIRM_TYPES[0],
          name: "",
          mainPhone: "",
          email: "",
          piiValue: undefined,
          piiExpiry: null,
          panelApproved: false,
          panelApprovedDate: null,
          distressedOnly: false,
          active: true,
          noFurtherInstructions: false,
          notes: "",
          contacts: [blankContact(true)],
          addresses: [blankAddress(true)],
          bankAccounts: [],
        })
      );
    }
  }, [firmId]);

  if (loading) return <LoaderSkeleton />;
  // On the very first paint of an Add, the draft is still null because the effect that seeds it
  // has not run yet. Only a real load failure gets the error state — otherwise that first paint
  // flashed "Couldn't load this company" before the empty form appeared.
  if (!draft) {
    if (!loadError) return <LoaderSkeleton />;
    return (
      <div className={classes.validationSummary}>
        <ErrorOutlineIcon className={classes.validationIcon} />
        <span>
          {loadError.message || "Couldn't load this company. Please try again."}
        </span>
      </div>
    );
  }

  const setField = (field, value) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  // The warning only stands while it still describes the (name, type) pair currently in the
  // draft — BOTH, not just the name (R12): switching Type away from the one that was checked
  // must invalidate it too. Derived rather than stored, so a stale result can never be rendered
  // or block a save.
  const activeDuplicate =
    duplicateOf &&
    isSameFirmIdentity(
      { name: duplicateOf.checkedName, type: duplicateOf.checkedType },
      { name: draft.name, type: draft.type }
    )
      ? duplicateOf
      : null;

  /**
   * The actual name-available round trip, shared by the name field's blur handler and the Type
   * select's onChange (FIRM-DUPLICATE-NAMES-014, step 5: changing Type RE-RUNS the check
   * immediately rather than waiting for the next blur — friendlier, since the user learns
   * straight away whether the name is taken under the type they just picked, at the cost of one
   * more request than the "wait for blur" alternative). This is a convenience — the server's 409
   * is what actually protects the data, which is why every failure path here is silent.
   */
  const runNameCheck = async (name, type) => {
    const seq = nameCheckSeq.current + 1;
    nameCheckSeq.current = seq;
    setCheckingName(true);
    try {
      const result = await api.checkFirmNameAvailable({
        name,
        type,
        excludeFirmId: isEdit ? firmId : undefined,
      });
      if (seq !== nameCheckSeq.current) return; // superseded by a later check
      setDuplicateOf(
        result && result.available === false
          ? {
              existingFirmId: result.existingFirmId || null,
              // Fall back to what was typed only if the server sent no stored name.
              existingFirmName: result.existingFirmName || name,
              checkedName: name,
              checkedType: type,
            }
          : null
      );
    } catch {
      // Swallowed on purpose: an unreachable lookup must leave the form fully usable and
      // saveable. The server still validates on save.
      if (seq === nameCheckSeq.current) setDuplicateOf(null);
    } finally {
      if (seq === nameCheckSeq.current) setCheckingName(false);
    }
  };

  /** R5: check the name (and its type) when the Company Name field loses focus. */
  const handleNameBlur = async () => {
    const name = draft.name;
    const type = draft.type;

    // A blank name is validateFirmDraft's problem, not this one, and a missing type can't be
    // checked at all — the endpoint requires it (v1.14.0) and would only answer 400.
    if (!normaliseFirmName(name) || !type) {
      setDuplicateOf(null);
      return;
    }

    // On an edit, tabbing through the firm's own unchanged (name, type) must not fire a request:
    // it can never be a duplicate of itself, and excludeFirmId would rule it out anyway. Both
    // must be unchanged — a firm whose TYPE alone has moved needs a real check (R12).
    if (
      isEdit &&
      isSameFirmIdentity(
        { name, type },
        { name: loadedNameRef.current, type: loadedTypeRef.current }
      )
    ) {
      setDuplicateOf(null);
      return;
    }

    await runNameCheck(name, type);
  };

  /** Type select onChange: re-runs the check under the new type (see runNameCheck's comment). */
  const handleTypeChange = (nextType) => {
    setField("type", nextType);

    const name = draft.name;
    if (!normaliseFirmName(name) || !nextType) return;

    // Moved back onto the firm's own loaded (name, type): nothing to check, and the derived
    // activeDuplicate above already stops rendering any stale warning for the type just left.
    if (
      isEdit &&
      isSameFirmIdentity(
        { name, type: nextType },
        { name: loadedNameRef.current, type: loadedTypeRef.current }
      )
    ) {
      return;
    }

    runNameCheck(name, nextType);
  };

  const handleSave = async () => {
    const { valid, missing: problems } = validateFirmDraft(draft);
    if (!valid) {
      setMissing(problems);
      revealValidationSummary();
      return;
    }
    // R6: the warning becomes a blocker at Save. Checked after validateFirmDraft so a form that
    // is both incomplete and duplicated reports the fixable problems first — the same
    // 400-before-409 precedence the API uses.
    if (activeDuplicate) {
      setMissing([duplicateNameMessage(activeDuplicate.existingFirmName)]);
      revealValidationSummary();
      return;
    }
    setMissing([]);
    setSaving(true);
    const payload = {
      type: draft.type,
      name: draft.name,
      mainPhone: draft.mainPhone,
      email: draft.email,
      piiValue: draft.piiValue ? Number(draft.piiValue) : undefined,
      piiExpiry: draft.piiExpiry || undefined,
      panelApproved: draft.panelApproved,
      panelApprovedDate: draft.panelApprovedDate || undefined,
      distressedOnly: draft.distressedOnly,
      active: draft.active,
      noFurtherInstructions: draft.noFurtherInstructions,
      notes: draft.notes,
      contacts: stripKeys(draft.contacts),
      addresses: stripKeys(draft.addresses),
      bankAccounts: draft.bankAccounts.map(toBankAccountUpsert),
    };
    try {
      const result = isEdit
        ? await api.updateFirm(firmId, payload)
        : await api.createFirm(payload);
      onSaved(result);
    } catch (err) {
      setMissing(
        err.fieldErrors?.length
          ? err.fieldErrors.map((fe) => `${fe.field}: ${fe.message}`)
          : [err.message || "Save failed"]
      );
      revealValidationSummary();
    } finally {
      setSaving(false);
    }
  };

  const showPII = draft.type !== "BorrowerSolicitor";
  const showDistressedToggle = !DISTRESSED_FIRM_TYPES.includes(draft.type);

  return (
    <div className={classes.formShell}>
      <div className={classes.formTopBar}>
        <button
          type="button"
          aria-label="Back"
          title="Back"
          className={classes.formBackButton}
          onClick={onCancel}
          disabled={saving}
        >
          <ArrowBackIcon fontSize="small" />
        </button>
        <Typography component="h3" className={classes.formTitle}>
          {isEdit ? "Edit Company" : "Add Company"}
        </Typography>
      </div>

      <div className={classes.formBody} ref={bodyRef}>
        {missing.length > 0 && (
          <div className={classes.validationSummary}>
            <ErrorOutlineIcon className={classes.validationIcon} />
            <span>Please complete the following: {missing.join("; ")}.</span>
          </div>
        )}

        <div
          className={clsx(classes.sectionHeading, classes.sectionHeadingFirst)}
        >
          Company details
        </div>
        <div className={classes.sectionCard}>
          <div className={classes.formGrid}>
            <A_SimpleSelect
              id="firm-type"
              label="Type"
              className={classes.field}
              size="small"
              style={{ margin: 0, width: "100%" }}
              defaultValue={draft.type}
              options={FIRM_TYPES.map((type) => ({
                value: type,
                label: firmTypeLabel(type),
              }))}
              optionLabel="label"
              optionValue="value"
              onChange={handleTypeChange}
            />
            <div className={classes.duplicateNameCell}>
              <A_TextField
                id="firm-name"
                label="Company Name *"
                className={classes.field}
                defaultValue={draft.name}
                onChange={(e) => {
                  const next = e.target.value;
                  setField("name", next);
                  // Clear the warning the moment the name stops matching the (name, type) pair
                  // that was checked. No second request: the result already carries what it
                  // describes, and the derived activeDuplicate above would suppress a stale
                  // result anyway — this just keeps duplicateOf itself tidy.
                  setDuplicateOf((prev) =>
                    prev &&
                    !isSameFirmIdentity(
                      { name: prev.checkedName, type: prev.checkedType },
                      { name: next, type: draft.type }
                    )
                      ? null
                      : prev
                  );
                }}
                onBlur={handleNameBlur}
                // Only endAdornment — NOT the whole InputProps. A_TextField composes its own
                // adornment around this one (A_TextField.jsx:297), so replacing InputProps
                // wholesale would drop the numeric spinner and A_FormHelper for this field.
                InputProps={{
                  endAdornment: checkingName ? (
                    <CircularProgress
                      color="inherit"
                      size={16}
                      aria-label="Checking company name"
                    />
                  ) : null,
                }}
                fullWidth
                size="small"
                margin="none"
                variant="outlined"
              />
              {activeDuplicate && (
                <div className={classes.duplicateNameWarning} role="status">
                  <ErrorOutlineIcon
                    className={classes.duplicateNameWarningIcon}
                  />
                  <span>
                    {duplicateNameMessage(activeDuplicate.existingFirmName)}
                    {/* Suppressed when the server sent no id — the lookup can report a name as
                        taken without one, and a control that opens an empty dialog is worse than
                        no control. */}
                    {activeDuplicate.existingFirmId && (
                      <button
                        type="button"
                        className={classes.duplicateNameViewLink}
                        onClick={() =>
                          setViewingFirmId(activeDuplicate.existingFirmId)
                        }
                      >
                        View company
                      </button>
                    )}
                  </span>
                </div>
              )}
            </div>
            <A_TextField
              id="firm-mainPhone"
              label="Main Contact Number *"
              className={classes.field}
              defaultValue={draft.mainPhone}
              onChange={(e) => setField("mainPhone", e.target.value)}
              fullWidth
              size="small"
              margin="none"
              variant="outlined"
            />
            <A_TextField
              id="firm-email"
              label="Company Email Address *"
              className={classes.field}
              defaultValue={draft.email}
              onChange={(e) => setField("email", e.target.value)}
              fullWidth
              size="small"
              margin="none"
              variant="outlined"
            />
          </div>
        </div>

        <ContactRowsEditor
          contacts={draft.contacts}
          onChange={(contacts) => setDraft((prev) => ({ ...prev, contacts }))}
        />
        <AddressRowsEditor
          addresses={draft.addresses}
          onChange={(addresses) => setDraft((prev) => ({ ...prev, addresses }))}
        />
        <BankAccountRowsEditor
          bankAccounts={draft.bankAccounts}
          onChange={(bankAccounts) =>
            setDraft((prev) => ({ ...prev, bankAccounts }))
          }
        />

        {showPII && (
          <>
            <div className={classes.sectionHeading}>
              Professional indemnity insurance &amp; panel
            </div>
            <div className={classes.sectionCard}>
              <div className={classes.formGrid}>
                <A_TextField
                  id="firm-piiValue"
                  label="PII Value (£)"
                  className={classes.field}
                  type="number"
                  inputType="value"
                  defaultValue={draft.piiValue}
                  onChange={(e) => setField("piiValue", e.target.value)}
                  fullWidth
                  size="small"
                  margin="none"
                  variant="outlined"
                />
                <A_DatePicker
                  id="firm-piiExpiry"
                  label="PII Expiry"
                  className={classes.field}
                  fullWidth
                  inputVariant="outlined"
                  margin="none"
                  size="small"
                  defaultValue={draft.piiExpiry}
                  onChange={(epochSeconds) =>
                    setField("piiExpiry", epochSecondsToIso(epochSeconds))
                  }
                />
                <A_DatePicker
                  id="firm-panelApprovedDate"
                  label="Panel Approved Date"
                  className={classes.field}
                  fullWidth
                  inputVariant="outlined"
                  margin="none"
                  size="small"
                  defaultValue={draft.panelApprovedDate}
                  onChange={(epochSeconds) =>
                    setField(
                      "panelApprovedDate",
                      epochSecondsToIso(epochSeconds)
                    )
                  }
                />
              </div>
            </div>
          </>
        )}

        <div className={classes.sectionHeading}>Notes &amp; status</div>
        <div className={classes.sectionCard}>
          <A_TextField
            id="firm-notes"
            label="Notes"
            className={classes.field}
            defaultValue={draft.notes}
            onChange={(e) => setField("notes", e.target.value)}
            fullWidth
            multiline
            rows={3}
            size="small"
            margin="none"
            variant="outlined"
          />

          <div className={classes.formFooter}>
            {showPII && (
              <span className={classes.toggleWrap}>
                <A_Switch
                  label="Panel Approved"
                  labelPlacement="end"
                  size="small"
                  checked={Boolean(draft.panelApproved)}
                  onChange={(e) => setField("panelApproved", e.target.checked)}
                />
              </span>
            )}
            {showDistressedToggle && (
              <span className={classes.toggleWrap}>
                <A_Switch
                  label="Distressed Scenarios Only"
                  labelPlacement="end"
                  size="small"
                  checked={Boolean(draft.distressedOnly)}
                  onChange={(e) => setField("distressedOnly", e.target.checked)}
                />
              </span>
            )}
            <span className={classes.toggleWrap}>
              <A_Switch
                label="Active"
                labelPlacement="end"
                size="small"
                checked={Boolean(draft.active)}
                onChange={(e) => setField("active", e.target.checked)}
              />
            </span>
            <span className={classes.toggleWrap}>
              <A_Switch
                label="No Further Instructions"
                labelPlacement="end"
                size="small"
                checked={Boolean(draft.noFurtherInstructions)}
                onChange={(e) =>
                  setField("noFurtherInstructions", e.target.checked)
                }
              />
            </span>
          </div>
        </div>
      </div>

      <div className={classes.formActionBar}>
        <A_Button
          label="Cancel"
          size="small"
          premiumVariant="secondary"
          onClick={onCancel}
          disabled={saving}
        />
        <A_SecuredAction permission={pageSecurityActions.UPDATE}>
          <A_Button
            label="Save Company"
            size="small"
            premiumVariant="primary"
            startIcon={<CheckIcon fontSize="small" />}
            onClick={handleSave}
            disabled={saving}
          />
        </A_SecuredAction>
      </div>

      {/* Rendered inside the form's own tree, so opening and closing it cannot unmount FirmForm
          and cannot touch `draft`. That is the whole point: the user looks at the existing
          company and comes back to everything they had already typed. */}
      <ExistingFirmDialog
        open={Boolean(viewingFirmId)}
        firmId={viewingFirmId}
        onClose={() => setViewingFirmId(null)}
      />
    </div>
  );
};

FirmForm.propTypes = {
  firmId: PropTypes.string,
  defaultType: PropTypes.string,
  initialAppend: PropTypes.shape({
    appendContact: PropTypes.bool,
    appendAddress: PropTypes.bool,
    appendBankAccount: PropTypes.bool,
  }),
  onCancel: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

FirmForm.defaultProps = {
  initialAppend: {},
};

export default FirmForm;
